<script lang="ts">
import type { TabsRootEmits, TabsRootProps } from 'reka-ui'
import type { Component } from 'vue'
import type { Color } from '../styles/colors'

export interface TabsItem {
  label?: string
  icon?: string
  chip?: string | number
  /** Keeps the chip coloured whatever the tab state, for chips that carry a status */
  chipColor?: Color
  value?: string | number
  disabled?: boolean
  class?: string | object
  [key: string]: any
}

export interface TabsProps<T extends TabsItem = TabsItem> extends Pick<TabsRootProps<string | number>, 'defaultValue' | 'modelValue' | 'unmountOnHide'> {
  as?: string | Component
  items?: T[]
  size?: 'md' | 'lg'
  orientation?: 'horizontal' | 'vertical'
  class?: string | object
}

export interface TabsEmits extends TabsRootEmits<string | number> {}

type SlotProps<T extends TabsItem> = (props: { item: T, index: number }) => any

export interface TabsSlots<T extends TabsItem = TabsItem> {
  content: SlotProps<T>
}

const ui = {
  root: 'flex items-center gap-2',
  list: 'relative flex group border-gray-300',
  indicator: 'absolute transition-[translate,width] duration-200 rounded-full bg-primary-500',
  trigger: 'group relative inline-flex items-center min-w-0 data-[state=inactive]:text-muted disabled:cursor-not-allowed disabled:opacity-30 transition-colors data-[state=active]:text-secondary-900 text-secondary-700 hover:text-secondary-800',
  content: 'focus:outline-none w-full',
  leadingIcon: 'shrink-0',
  label: 'truncate',
  chip: {
    base: 'text-xs font-medium rounded-3px px-1 transition-color',
    inactive: 'bg-secondary-100 text-secondary-600 group-hover:text-secondary-800',
    active: 'bg-info-50 text-primary-500',
    colored: 'text-{color}-800 bg-{color}-100',
  },
  orientation: {
    horizontal: {
      root: 'flex-col',
      list: 'w-full pb-1.75 border-b border-b-1px -mb-px',
      indicator: 'left-0 w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position] -bottom-2px h-2px',
      trigger: 'justify-center',
    },
    vertical: {
      root: '',
      list: 'flex-col pl-1.75 border-s border-s-1px -ms-px',
      indicator: 'top-0 h-[--reka-tabs-indicator-size] translate-y-[--reka-tabs-indicator-position] -start-2px w-2px',
      trigger: '',
    },
  },
  size: {
    md: {
      trigger: 'px-2 py-1 text-md font-medium gap-1.5',
      leadingIcon: 'size-3',
    },
    lg: {
      trigger: 'px-2 py-1.25 font-medium gap-2',
      leadingIcon: 'size-3.5',
    },
  },
}
</script>

<script setup lang="ts" generic="T extends TabsItem">
import { reactivePick } from '@vueuse/core'
import { TabsContent, TabsIndicator, TabsList, TabsRoot, TabsTrigger, useForwardPropsEmits } from 'reka-ui'
import AwIcon from './AwIcon.vue'

const props = withDefaults(defineProps<TabsProps<T>>(), {
  defaultValue: '0',
  orientation: 'horizontal',
  unmountOnHide: true,
  size: 'md',
})
const emits = defineEmits<TabsEmits>()
defineSlots<TabsSlots<T>>()

function getChipClass(item: T, index: number, modelValue: string | number | undefined) {
  if (item.chipColor)
    return ui.chip.colored.replaceAll('{color}', item.chipColor)
  return [item.value, String(index)].includes(modelValue) ? ui.chip.active : ui.chip.inactive
}

const rootProps = useForwardPropsEmits(reactivePick(props, 'as', 'modelValue', 'defaultValue', 'orientation', 'unmountOnHide'), emits)
</script>

<template>
  <TabsRoot v-slot="{ modelValue }" v-bind="rootProps" :class="[ui.root, ui.orientation[orientation].root, props.class]">
    <TabsList :class="[ui.list, ui.orientation[orientation].list]">
      <TabsIndicator :class="[ui.indicator, ui.orientation[orientation].indicator]" />
      <TabsTrigger v-for="(item, index) of items" :key="index" :value="item.value || String(index)" :disabled="item.disabled" :class="[ui.trigger, ui.orientation[orientation].trigger, ui.size[size].trigger]">
        <AwIcon v-if="item.icon" :name="item.icon" :class="[ui.leadingIcon, ui.size[size].leadingIcon]" />
        <span v-if="item.label" :class="ui.label">
          {{ item.label }}
        </span>
        <span v-if="item.chip" :class="[ui.chip.base, getChipClass(item, index, modelValue)]">{{ item.chip }}</span>
      </TabsTrigger>
    </TabsList>
    <TabsContent v-for="(item, index) of items" :key="index" :value="item.value || String(index)" :class="ui.content">
      <slot name="content" :item :index>
        {{ item.content }}
      </slot>
    </TabsContent>
  </TabsRoot>
</template>
